import { Vals } from '~lib';

export namespace User {
  /**
   * @desc WorkOS environment-level role slugs. A membership carries either exactly one
   * internal-group role (staff, cross-deployment, no pending state) or product-group roles (at
   * most one per product group, independently combinable and discrete — no role implies another),
   * with the permissions a token bears being the union across the assigned roles
   * (`refactor-roles-internal-product-split`, superseding design D4 of H2-171).
   */
  export const roles = {
    InternalUser: 'internal-user',
    InternalAdmin: 'internal-admin',
    AgentPending: 'agent-pending',
    AgentBeta: 'agent-beta',
    /** @desc Reserved for the platform beta phase — declared, not yet assigned by app flows. */
    PlatformPending: 'platform-pending',
    /** @desc Reserved for the platform beta phase — declared, not yet assigned by app flows. */
    PlatformBeta: 'platform-beta',
    /** @desc Reserved final-launch tier-1 licence — declared, not yet assigned by app flows. */
    PlatformBasic: 'platform-basic',
    /** @desc Reserved final-launch tier-2 licence — declared, not yet assigned by app flows. */
    PlatformPremium: 'platform-premium',
  } as const;
  export type Role = Vals<typeof roles>;

  /**
   * @desc Permission slugs attached to the roles above. These — never role slugs — are what the
   * guards consult, so a role can be renamed or split without touching an enforcement point.
   */
  export const permissions = {
    AgentAccess: 'agent:access',
    PlatformAccess: 'platform:access',
    AdminAccess: 'admin:access',
  } as const;
  export type Permission = Vals<typeof permissions>;

  /**
   * @desc The role groups. The internal group is exclusive of the product groups: a membership
   * carries either exactly one internal-group role or product-group roles (one per group at most).
   */
  export const roleGroups = {
    Internal: 'internal',
    Agent: 'agent',
    Platform: 'platform',
  } as const;
  export type RoleGroup = Vals<typeof roleGroups>;

  /**
   * @desc Role slugs belonging to each group, in ascending order of access. Used by the WorkOS
   * gateway to replace a membership's role within one group without disturbing the others.
   */
  export const rolesByGroup: Record<RoleGroup, readonly Role[]> = {
    [roleGroups.Internal]: [roles.InternalUser, roles.InternalAdmin],
    [roleGroups.Agent]: [roles.AgentPending, roles.AgentBeta],
    [roleGroups.Platform]: [
      roles.PlatformPending,
      roles.PlatformBeta,
      roles.PlatformBasic,
      roles.PlatformPremium,
    ],
  };

  /** @desc The group a role slug belongs to, or `null` for a slug outside the product groups. */
  export const roleGroupOf = (slug: string): RoleGroup | null => {
    for (const group of Object.values(roleGroups)) {
      if (rolesByGroup[group].some((role) => role === slug)) return group;
    }
    return null;
  };

  /** @desc Type guard for a slug that is a member of the given group. */
  export const isRoleInGroup = (slug: string, group: RoleGroup): slug is Role =>
    roleGroupOf(slug) === group;

  /**
   * @desc The one role a membership holds in the given group, or `null` when it carries none (a
   * membership with no platform-group role has no platform state at all — design D4).
   */
  export const roleInGroup = (slugs: string[], group: RoleGroup): Role | null =>
    slugs.find((slug): slug is Role => isRoleInGroup(slug, group)) ?? null;

  export type Model<D extends string | Date = Date> = {
    _id: string;
    email: string;
    name: {
      first: string;
      last: string;
      formatted: string;
    };
    roles: Role[];
    createdAt: D;
    updatedAt: D;
  };

  /**
   * @desc A row of the admin user-management panel: the WorkOS user, the organisation whose
   * membership carries their roles, and the role each product group is set to.
   *
   * Derived per request from WorkOS (the system of record for membership and roles) — never from
   * the Mongo `users` document, whose `roles` field is data rather than an enforcement point.
   */
  export type Managed = {
    workosUserId: string;
    /**
     * @desc The portal's internal user id — the id keying the Mongo `users` collection and
     * `conversation.refs.userId` (the token's `external_id` claim when set, otherwise the WorkOS
     * `sub`; see `apps/backend/src/lib/workos.ts`). Undefined when no portal user document has
     * been resolved for this membership yet, i.e. the person has never signed in.
     */
    userId?: string;
    /** @desc The WorkOS `external_id` (the legacy internal id for migrated users), when set. */
    externalId?: string;
    membershipId: string;
    email: string;
    name: string | null;
    organisation: { id: string; name: string };
    /** @desc The single staff role, exclusive of the product roles below; `null` for externals. */
    internalRole: Role | null;
    /** @desc Every external membership carries one; `null` for staff (internal role instead). */
    agentRole: Role | null;
    /** @desc `null` means no platform state — the ordinary case for a subscriber. */
    platformRole: Role | null;
    /** @desc Slugs outside every group, e.g. the WorkOS default `member`. */
    otherRoles: string[];
    /** @desc WorkOS membership status (`active` | `inactive` | `pending`). */
    status: string;
    createdAt: string;
  };

  /**
   * @desc A pending internal-organisation invitation: sent but not yet accepted. Surfaced on the
   * admin Team page so invited staff are visible before onboarding completes.
   */
  export type PendingInvitation = {
    invitationId: string;
    email: string;
    organisation: { id: string; name: string };
    /** @desc The internal role the invitee will hold on acceptance; `null` if it was changed to
     * something outside the internal group after sending. */
    role: Role | null;
    expiresAt: string;
    createdAt: string;
  };

  /**
   * @desc A subscriber-organisation invitation that lapsed before it was accepted and has not
   * been superseded by a newer one for the same address. WorkOS can only resend a pending
   * invitation, so the admin Subscribers page offers a reissue (a fresh invitation) instead.
   */
  export type ExpiredInvitation = {
    invitationId: string;
    email: string;
    organisation: { id: string; name: string };
    /** @desc The role slug the invitee would have joined on; carried over on reissue. */
    roleSlug: string | null;
    expiresAt: string;
    createdAt: string;
  };

  export const toSerial = (u: Model): Model<string> => ({
    ...u,
    createdAt: u.createdAt.toISOString(),
    updatedAt: u.updatedAt.toISOString(),
  });
}
